import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AnalysisArtifacts } from '../../types/conversation';
import { downloadAnalysisArtifact } from '../../utils/downloadAnalysisArtifact';

interface AnalysisArtifactsBarProps {
  analysisArtifacts?: AnalysisArtifacts | null;
}

type ArtifactFormat = 'xlsx' | 'csv' | 'pdf' | 'json';

interface ArtifactDescriptor {
  format: ArtifactFormat;
  label: string;
  icon: string;
  defaultFilename: string;
  downloadUrl: string;
  fileName: string;
  base64?: string;
  mimeType?: string;
}

const FORMAT_LABEL: Record<ArtifactFormat, string> = {
  xlsx: 'Planilha XLSX',
  csv: 'CSV',
  pdf: 'PDF',
  json: 'JSON',
};

const FORMAT_ICON: Record<ArtifactFormat, string> = {
  xlsx: '📊',
  csv: '📄',
  pdf: '📕',
  json: '🧾',
};

const FORMAT_DEFAULT_NAME: Record<ArtifactFormat, string> = {
  xlsx: 'relatorio.xlsx',
  csv: 'relatorio.csv',
  pdf: 'relatorio.pdf',
  json: 'relatorio.json',
};

const FORMAT_DEFAULT_MIME: Record<ArtifactFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  pdf: 'application/pdf',
  json: 'application/json',
};

const collectArtifacts = (
  artifacts: AnalysisArtifacts
): ArtifactDescriptor[] => {
  const items: ArtifactDescriptor[] = [];
  (Object.keys(FORMAT_LABEL) as ArtifactFormat[]).forEach(format => {
    const downloadUrl = String(
      artifacts[`${format}_download_url` as keyof AnalysisArtifacts] || ''
    ).trim();
    const base64 = String(
      artifacts[`${format}_base64` as keyof AnalysisArtifacts] || ''
    ).trim();
    if (!downloadUrl && !base64) {
      return;
    }
    items.push({
      format,
      label: FORMAT_LABEL[format],
      icon: FORMAT_ICON[format],
      defaultFilename: FORMAT_DEFAULT_NAME[format],
      downloadUrl,
      base64,
      fileName: String(
        artifacts[`${format}_file_name` as keyof AnalysisArtifacts] ||
          FORMAT_DEFAULT_NAME[format]
      ),
      mimeType: String(
        artifacts[`${format}_mime_type` as keyof AnalysisArtifacts] ||
          FORMAT_DEFAULT_MIME[format]
      ),
    });
  });
  return items;
};

const triggerBase64Download = (
  base64: string,
  filename: string,
  mimeType: string
): void => {
  const sanitized = base64.replace(/^data:[^;]+;base64,/i, '').replace(/\s+/g, '');
  const binary = window.atob(sanitized);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const blob = new Blob([bytes], { type: mimeType || 'application/octet-stream' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
};

/**
 * Renders a row of download chips for any artefact attached to the
 * assistant message. Used in the regular (non-canvas) chat bubble so the
 * user sees a real clickable link instead of just the LLM's prose
 * mentioning that a file was generated.
 *
 * The download endpoint requires a Bearer token, so we fetch the file in
 * JS (`downloadAnalysisArtifact`) instead of relying on `<a download>`.
 */
export const AnalysisArtifactsBar: React.FC<AnalysisArtifactsBarProps> = ({
  analysisArtifacts,
}) => {
  const [pending, setPending] = useState<ArtifactFormat | null>(null);

  if (!analysisArtifacts) {
    return null;
  }

  const items = collectArtifacts(analysisArtifacts);
  if (items.length === 0) {
    return null;
  }

  const handleDownload = async (item: ArtifactDescriptor): Promise<void> => {
    setPending(item.format);
    try {
      if (item.base64) {
        triggerBase64Download(
          item.base64,
          item.fileName || item.defaultFilename,
          item.mimeType || ''
        );
        return;
      }
      await downloadAnalysisArtifact(
        item.downloadUrl,
        item.fileName || item.defaultFilename
      );
    } catch {
      toast.error('Não consegui baixar o arquivo. Tente novamente em instantes.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {items.map(item => {
        const isPending = pending === item.format;
        return (
          <button
            key={item.format}
            type="button"
            disabled={isPending}
            onClick={() => {
              void handleDownload(item);
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-orange-200 bg-orange-50 hover:bg-orange-100 disabled:opacity-60 disabled:cursor-not-allowed text-orange-700 text-sm font-medium transition-colors dark:border-orange-700 dark:bg-orange-900/20 dark:hover:bg-orange-900/30 dark:text-orange-200"
            title={`Baixar ${item.fileName}`}
          >
            <span aria-hidden>{item.icon}</span>
            <span>
              {isPending ? 'Baixando...' : `Baixar ${item.label}`}
            </span>
            {item.fileName && item.fileName !== item.defaultFilename && (
              <span className="text-orange-500/80 dark:text-orange-300/80 text-xs font-normal max-w-[220px] truncate">
                {item.fileName}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
